import { Component, OnInit } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import Swal from 'sweetalert2';
import { AuthService } from '../../../core/auth.service';
import { PayrollService } from '../payroll/payroll.service';
import { StaffService } from '../staff.service';

@Component({
  selector: 'app-staff-performance',
  templateUrl: './staff-performance.component.html',
  styleUrls: ['./staff-performance.component.scss']
})
export class StaffPerformanceComponent implements OnInit {
  loading = false;
  staff: any[] = [];
  slips: any[] = [];
  requests: any[] = [];

  constructor(
    private staffService: StaffService,
    private payrollService: PayrollService,
    private auth: AuthService
  ) {}

  ngOnInit(): void {
    this.loadPerformance();
  }

  loadPerformance(): void {
    this.loading = true;
    forkJoin({
      staff: this.canViewStaffDirectory() ? this.staffService.getAll() : of([]),
      slips: this.canViewPayroll() ? this.payrollService.getSlips() : of([]),
      requests: this.canViewRequests() ? this.staffService.getAllRequests() : of([])
    }).subscribe({
      next: ({ staff, slips, requests }) => {
        this.staff = staff || [];
        this.slips = slips || [];
        this.requests = requests || [];
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        Swal.fire('Error', err?.error?.message || 'Failed to load staff performance.', 'error');
      }
    });
  }

  get totalNetPay(): number {
    return this.slips.reduce((sum, row) => sum + Number(row?.netAmount || 0), 0);
  }

  get totalDeliveries(): number {
    return this.slips.reduce((sum, row) => sum + Number(row?.deliveriesCount || 0), 0);
  }

  get averageNetPay(): number {
    return this.slips.length ? this.totalNetPay / this.slips.length : 0;
  }

  get topEarners(): any[] {
    return [...this.slips]
      .sort((a, b) => Number(b?.netAmount || 0) - Number(a?.netAmount || 0))
      .slice(0, 6);
  }

  get topDeliveryStaff(): any[] {
    return [...this.slips]
      .sort((a, b) => Number(b?.deliveriesCount || 0) - Number(a?.deliveriesCount || 0))
      .slice(0, 6);
  }

  get requestPressure(): Array<{ label: string; value: number }> {
    const map = new Map<string, number>();
    this.requests.forEach((row) => {
      const key = String(row?.requestType || 'Other').trim() || 'Other';
      map.set(key, (map.get(key) || 0) + 1);
    });
    return Array.from(map.entries())
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 6);
  }

  get payrollCoverage(): number {
    const uniqueStaff = new Set(this.slips.map((row) => row?.staffId).filter((id) => !!id));
    return uniqueStaff.size;
  }

  private canViewStaffDirectory(): boolean {
    return this.auth.hasAnyPermission(['staff.view', 'staff.manage']);
  }

  private canViewPayroll(): boolean {
    return this.auth.hasAnyPermission(['staff.view', 'staff.manage', 'staff.request.approve', 'manage_users']);
  }

  private canViewRequests(): boolean {
    return this.auth.hasAnyPermission(['staff.request.view', 'staff.request.approve', 'staff.manage']);
  }
}
